import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Search, X } from "lucide-react";

const TABS = [
  { k: "all", label: "All" },
  { k: "pending", label: "Pending" },
  { k: "confirmed", label: "Confirmed" },
  { k: "rescheduled", label: "Rescheduled" },
  { k: "completed", label: "Completed" },
  { k: "cancelled", label: "Cancelled" },
  { k: "no_show", label: "No-show" },
];

export default function AppointmentsPage() {
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/appointments", { params: { status, search: search || undefined } });
      setList(data);
    } finally { setLoading(false); }
  }, [status, search]);

  useEffect(() => { load(); }, [load]);

  async function changeStatus(id, newStatus, note = "") {
    try {
      await api.patch(`/appointments/${id}/status`, { status: newStatus, note: note || null });
      toast.success(`Marked ${newStatus.replace("_", " ")}`);
      load();
      if (open?.id === id) {
        const { data } = await api.get(`/appointments/${id}`);
        setOpen(data);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="space-y-5" data-testid="appointments-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold text-slate-900">Appointments</h1>
          <p className="text-slate-500 text-sm">Confirm, reschedule, cancel, and track every booking</p>
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search patient, phone, DP-ref" className="pl-8 pr-3 py-2 border border-slate-300 rounded-lg text-sm bg-white w-72" data-testid="appointments-search-input" />
        </div>
      </div>
      <div className="flex gap-1 flex-wrap">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setStatus(t.k)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${status === t.k ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"}`} data-testid={`tab-${t.k}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {loading ? <div className="p-6 text-slate-500">Loading...</div> : list.length === 0 ? <div className="p-6 text-slate-500">No appointments match.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr><th className="text-left px-5 py-3">Reference</th><th className="text-left px-5 py-3">Patient</th><th className="text-left px-5 py-3">Date / Time</th><th className="text-left px-5 py-3">Type</th><th className="text-left px-5 py-3">Status</th><th className="text-right px-5 py-3">Actions</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {list.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50/60 cursor-pointer" onClick={() => setOpen(a)} data-testid={`appointment-row-${a.reference}`}>
                    <td className="px-5 py-3 font-mono text-xs text-slate-700">{a.reference}</td>
                    <td className="px-5 py-3"><div className="font-medium text-slate-900">{a.patient_name}</div><div className="text-xs text-slate-500">{a.phone}</div></td>
                    <td className="px-5 py-3">{a.date}<div className="text-xs text-slate-500">{a.start_time} – {a.end_time}</div></td>
                    <td className="px-5 py-3 text-slate-700">{a.consultation_type}</td>
                    <td className="px-5 py-3"><StatusBadge status={a.status} /></td>
                    <td className="px-5 py-3 text-right">
                      <button className="text-emerald-700 hover:text-emerald-800 font-medium text-sm" onClick={(e) => { e.stopPropagation(); setOpen(a); }}>Open</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {open && <AppointmentDrawer appt={open} setAppt={setOpen} onClose={() => setOpen(null)} onAction={changeStatus} onReload={load} />}
    </div>
  );
}

function AppointmentDrawer({ appt, setAppt, onClose, onAction, onReload }) {
  const [note, setNote] = useState("");
  const [newDate, setNewDate] = useState(appt.date);
  const [slots, setSlots] = useState([]);
  const [newTime, setNewTime] = useState("");
  const [showResched, setShowResched] = useState(false);

  async function loadSlots(d) {
    const { data } = await api.get(`/availability/slots`, { params: { date: d } });
    setSlots(data.slots || []);
  }
  useEffect(() => { if (showResched && newDate) loadSlots(newDate); }, [showResched, newDate]);

  async function submitReschedule() {
    if (!newTime) return toast.error("Pick a time");
    try {
      await api.patch(`/appointments/${appt.id}/reschedule`, { date: newDate, start_time: newTime, note: note || null });
      toast.success("Rescheduled");
      onReload();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function addNote() {
    if (!note.trim()) return;
    try {
      await api.post(`/appointments/${appt.id}/notes`, { note });
      toast.success("Note added");
      setNote("");
      const { data } = await api.get(`/appointments/${appt.id}`);
      setAppt(data);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const terminal = ["cancelled", "completed", "no_show"].includes(appt.status);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose} data-testid="appointment-drawer">
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="font-mono text-xs text-slate-500">{appt.reference}</div>
            <h3 className="text-xl font-display font-bold text-slate-900 mt-0.5">{appt.patient_name}</h3>
            <div className="text-sm text-slate-500">{appt.phone}{appt.email ? ` · ${appt.email}` : ""}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X size={18} /></button>
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
          <Info label="Date"><span>{appt.date}</span></Info>
          <Info label="Time"><span>{appt.start_time} – {appt.end_time}</span></Info>
          <Info label="Type"><span>{appt.consultation_type}</span></Info>
          <Info label="Status"><StatusBadge status={appt.status} /></Info>
        </div>
        {appt.reason && <div className="mt-4 bg-slate-50 rounded-xl p-4 text-sm"><div className="text-xs text-slate-500 mb-1">Reason</div>{appt.reason}</div>}
        <div className="mt-6 flex flex-wrap gap-2">
          {!terminal && appt.status !== "confirmed" && <Btn onClick={() => onAction(appt.id, "confirmed")} tid="appointment-action-confirm">Confirm</Btn>}
          {!terminal && <Btn onClick={() => setShowResched((v) => !v)} variant="secondary" tid="appointment-action-reschedule">Reschedule</Btn>}
          {!terminal && <Btn onClick={() => onAction(appt.id, "completed")} variant="indigo" tid="appointment-action-complete">Complete</Btn>}
          {!terminal && <Btn onClick={() => onAction(appt.id, "no_show")} variant="slate" tid="appointment-action-noshow">No-show</Btn>}
          {!terminal && <Btn onClick={() => onAction(appt.id, "cancelled")} variant="rose" tid="appointment-action-cancel">Cancel</Btn>}
          {appt.status === "cancelled" && <Btn onClick={() => onAction(appt.id, "confirmed")} tid="appointment-action-restore">Restore &amp; Confirm</Btn>}
        </div>

        {showResched && (
          <div className="mt-5 border border-slate-200 rounded-xl p-4 bg-slate-50/60">
            <div className="font-semibold text-slate-800 mb-2 text-sm">Reschedule</div>
            <input type="date" data-testid="reschedule-date-input" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="input" />
            <div className="mt-3 grid grid-cols-4 gap-2">
              {slots.map((s) => (
                <button key={s.start_time} disabled={!s.available}
                  onClick={() => setNewTime(s.start_time)}
                  data-testid={`reschedule-slot-${s.start_time}`} className={`text-xs py-2 rounded-lg border ${newTime === s.start_time ? "bg-emerald-600 text-white border-emerald-600" : s.available ? "bg-white border-slate-200" : "bg-slate-50 text-slate-400 border-slate-100 cursor-not-allowed"}`}>
                  {s.start_time}
                </button>
              ))}
            </div>
            <button onClick={submitReschedule} className="mt-3 bg-sky-700 hover:bg-sky-800 text-white rounded-lg px-4 py-2 text-sm font-semibold" data-testid="reschedule-submit-btn">Confirm reschedule</button>
          </div>
        )}

        <div className="mt-6">
          <div className="font-semibold text-slate-800 text-sm">Internal notes</div>
          <div className="mt-2 space-y-2">
            {(appt.notes_internal || []).map((n) => (
              <div key={n.id} className="bg-slate-50 rounded-lg p-3 text-sm">
                <div className="text-xs text-slate-500">{new Date(n.at).toLocaleString()} · {n.by}</div>
                <div className="text-slate-800 mt-0.5">{n.text}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a private note" className="input flex-1" data-testid="note-input" />
            <button onClick={addNote} className="bg-slate-900 text-white rounded-lg px-4 text-sm font-semibold" data-testid="note-add-btn">Add</button>
          </div>
        </div>

        <div className="mt-6">
          <div className="font-semibold text-slate-800 text-sm">History</div>
          <div className="mt-2 space-y-1 text-xs">
            {(appt.history || []).map((h, i) => (
              <div key={i} className="text-slate-600"><span className="font-semibold capitalize">{h.status.replace("_", " ")}</span> · {new Date(h.at).toLocaleString()} · {h.by}{h.note ? ` — ${h.note}` : ""}</div>
            ))}
          </div>
        </div>

        <style>{`.input{width:100%;padding:.55rem .8rem;border:1px solid #CBD5E1;border-radius:.6rem;background:#fff;color:#0F172A;font-size:.9rem;}
        .input:focus{outline:none;border-color:#0D9488;box-shadow:0 0 0 3px rgb(13 148 136 / 0.15)}`}</style>
      </div>
    </div>
  );
}

function Info({ label, children }) {
  return <div><div className="text-xs text-slate-500">{label}</div><div className="font-medium text-slate-800 mt-0.5">{children}</div></div>;
}

function Btn({ onClick, children, variant = "emerald", tid }) {
  const map = {
    emerald: "bg-emerald-700 hover:bg-emerald-800 text-white",
    secondary: "bg-white hover:bg-slate-50 text-slate-800 border border-slate-300",
    indigo: "bg-indigo-700 hover:bg-indigo-800 text-white",
    slate: "bg-slate-700 hover:bg-slate-800 text-white",
    rose: "bg-rose-600 hover:bg-rose-700 text-white",
  };
  return <button onClick={onClick} data-testid={tid} className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${map[variant]}`}>{children}</button>;
}

function StatusBadge({ status }) {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    confirmed: "bg-emerald-100 text-emerald-800",
    rescheduled: "bg-sky-100 text-sky-800",
    cancelled: "bg-rose-100 text-rose-800",
    completed: "bg-indigo-100 text-indigo-800",
    no_show: "bg-slate-200 text-slate-700",
  };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${map[status] || "bg-slate-100 text-slate-700"}`} data-testid="appointment-status-badge">{(status || "").replace("_", " ")}</span>;
}
